import { Link } from "@mui/material";

const EffectiveOnline = () => {
  return (
    <div className="container--blog_post_page_wrapper">
      <section className="container--blog_post">
        <div className="container--post_content">
          <p className="text--post_paragraph">
            “All right, let’s go ahead and break into groups…”<br/><br/>
            That sentence can be a source of stress for the best of us. There
            are a wide range of skills, viewpoints, motives, and resources in
            any environment. Who will you work with? Do they know what they're
            doing? Will one person be doing all the work? How will it be
            organized? However, instead of looking at group work as a chore, it
            can be more productive to look at it as a chance to grow, pool
            resources, and make a more in-depth analysis of a subject. It can
            help students develop leadership, organizational, cooperative, and
            vital communication skills.{" "}
            <Link
              href="https://www.birmingham.ac.uk/schools/metallurgy-materials/about/cases/group-work/why.aspx"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more on the University of Birmingham's take on group work"
            >
              (University of Birmingham)
            </Link>{" "}
            Additionally, restrictions from COVID-19 have made group work
            virtually a requirement in many academic settings, necessitating
            that students band together in a safe environment to remain
            productive.
          </p>
          <p className="text--post_paragraph">
            As with many things, creating effective group work can be easier
            said than done. When working with a blend of personalities, a little
            thought and organization can go a long way towards getting the job
            done and help students be accountable to both themselves and their
            classmates. Below are five simple but effective tips to make the
            transition from in-person to virtual coursework go as smoothly as
            possible.
          </p>
          <h2 className="header header--page_title_h2_left">
            1. File-Sharing Tools
          </h2>
          <p className="text--post_paragraph">
            Communication is key to group work, so it is important that all
            students have access to the same academic resources. Utilizing a
            file-sharing application such as Google Suite or Evernote (which are
            free) helps groups stay organized and promotes a positive group
            experience. Canvas or Blackboard are provided by many school
            districts in the U.S. and offer opportunities for students to
            interact with members of their team and leave feedback on each
            other's work. Chat functions allow interactions in real-time,
            providing a substitute for in-person discussions, and facilitate
            collaboration throughout the project.{" "}
            <Link
              href="https://resilienteducator.com/classroom-resources/online-instructors-group-work/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more on effective online group work at Resilient Educator"
            >
              (Resilient Educator)
            </Link>
          </p>
          <p className="text--post_paragraph">
            File sharing applications also eliminate the need for emailing,
            uploading, or downloading files from multiple sources. All resources
            and documents are located in one place for everyone to access. This
            ensures that all group members have access to the most up-to-date
            version of the work.{" "}
            <Link
              href="https://www.poweradmin.com/blog/what-are-the-pros-and-cons-of-file-sharing/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Explore the pros and cons of file sharing on Power Admin"
            >
              (Power Admin)
            </Link>
          </p>
          <h2 className="header header--page_title_h2_left">
            2. Make It Manageable
          </h2>
          <p className="text--post_paragraph">
            Beginning a group project can be overwhelming. According to{" "}
            <Link
              href="https://www.researchgate.net/figure/Summary-of-Factors-That-Hinder-Success-in-Group-work-in-an-Online-Course_tbl2_285867454"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Study on factors hindering success in group work in online courses on Research Gate"
            >
              (Research Gate),
            </Link>
            &nbsp;62% of students experience some difficulty in understanding
            online projects. To avoid this, teachers can implement the concept
            of "scaffolding," which breaks down tasks into manageable chunks,
            and provides the appropriate tools for each class. This allows
            teachers to ensure that learning goals are met while building the
            foundation for the project from which the students can learn.
          </p>
          <p className="text--post_paragraph">
            Attention spans can also be shorter in an online environment, with
            most people losing focus after forty-five minutes or so.{" "}
            <Link
              href="https://www.forbes.com/sites/forbesbusinesscouncil/2020/10/13/how-to-make-online-learning-work-for-your-company-this-year/?sh=86114c856cd1"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
            >
              (Forbes)
            </Link>{" "}
            Teachers should keep their lectures brief, and provide the
            opportunity for more direct interaction between themselves and the
            students, breaking tasks into smaller sections and distributing them
            between specific members of the team. This will also allow for the
            teachers to provide regular assessments of student work to ensure
            their understanding of the topic.
          </p>
          <h2 className="header header--page_title_h2_left">
            3. Grow Together
          </h2>
          <p className="text--post_paragraph">
            In any class environment, there will be a spectrum of learning
            styles and abilities. Effective group work identifies each student's
            strengths and tailors team jobs appropriately. Additionally, there
            is a range of motivation within every group, with high-achievers
            often "taking charge," leaving less motivated students to hang out
            in the margins. Every team member should be assigned a designated
            role, which allows students to grow together as a group and makes
            sure each student is doing their part.
          </p>
          <p className="text--post_paragraph">
            Before any group project, teachers should train students on how to
            function in a group, which leads to measurable student objectives
            and outcomes. Delegating, communicating and goal setting are
            valuable parts of the learning process and will empower students to
            do more thoughtful and collaborative work. This ensures that all
            students are equally responsible for the group's output.{" "}
            <Link
              href="https://www.insidehighered.com/advice/2017/08/01/helping-diverse-learners-navigate-group-work-essay"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about helping diverse learners navigate group work on Inside Higher Ed"
            >
              (Inside Higher Ed)
            </Link>
          </p>

          <h2 className="header header--page_title_h2_left">
            4. Minute Taking
          </h2>

          <p className="text--post_paragraph">
            Assign a team member, or “secretary,” to take minutes of each
            meeting on platforms such as Zoom or Google Meet. “Minutes” are
            simply notes taken during the meeting and are essential for keeping
            track of who was in attendance, what happened, and who was assigned
            to take care of each task. While taking minutes, don’t write down
            every detail — just summarize the issue, taking care to include key
            points of important subjects.{" "}
            <Link
              href="https://www.resourcecentre.org.uk/information/taking-minutes/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
            >
              (Resource Centre)
            </Link>
          </p>
          <p className="text--post_paragraph">
            A written record is also helpful to students who were unable to
            attend the meeting, keeping them “in the loop.” This ensures all
            team members are held accountable for their work, and helps to track
            the team’s progress.
          </p>
          <h2 className="header header--page_title_h2_left">
            5. Team Building
          </h2>
          <p className="text--post_paragraph">
            Finding group project partners online is not an impossible task.
            While assigning students to groups can ensure that they are exposed
            to a diversity of ideas and backgrounds, it can be difficult if
            their work styles are not compatible or if the students simply do
            not get along.{" "}
            <Link
              href="https://www.insidehighered.com/advice/2017/08/01/helping-diverse-learners-navigate-group-work-essay"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
            >
              (Inside Higher Ed)
            </Link>
          </p>
          <p className="text--post_paragraph">
            Using an online platform such as{" "}
            <Link
              href="https://www.teammateme.com/about"
              target="_blank"
              rel="noreferrer noopener dofollow"
            >
              TeammateMe.com
            </Link>{" "}
            eliminates any barriers to effective group productivity and
            communication, even allowing students to filter potential partners
            by skill set, interest, and reputation scores. For students, it
            supports an engaging learning environment in a virtual platform and
            teaches them the skills they need to be effective team participants.
            Connection with peers is available 24/7, providing them with tools
            such as group chats, DMs, and the opportunity for constructive
            criticism/feedback in a safe, supportive atmosphere. They can find
            students with compatible working styles while encouraging diversity.
          </p>

          <h2 className="header header--page_title_h2_left">One More Thing…</h2>
          <p className="text--post_paragraph">
            The COVID-19 pandemic has necessitated isolation from friends,
            family, and peers. Students have had a particularly tough time, as
            in-person learning and socializing has been transferred to a
            solitary, online format. But it isn’t all bad news. Collaborating on
            group work allows students to grow together as they learn:
            connecting, facilitating discussion, and being exposed to new ideas
            and points of view which help develop skills that will serve them
            well later in life. When teachers facilitate group discussions,
            whether in-person or online, students will learn from each other as
            well as from any academic source, allowing them to move beyond
            working on a mere “group project” and become better thinkers,
            creators, and life-long learners. The world may have become a
            (hopefully temporarily) more isolated place, but collaborative
            platforms (such as{" "}
            <Link
              href="https://www.teammateme.com/about"
              target="_blank"
              rel="noreferrer noopener dofollow"
            >
              TeammateMe.com
            </Link>{" "}
            give people a chance to reach out, connect with others, and put
            their best work forward.
          </p>
        </div>
        <hr className="hr--form_break" />
      </section>
    </div>
  );
};

export default EffectiveOnline;
